import { useFocusEffect } from 'expo-router';
import { useCallback, useRef, useState } from 'react';
import { Input, type TamaguiElement, Text, XStack, YStack } from 'tamagui';

/**
 * PSOtpInput — a 6-digit one-time-code field (atom).
 *
 * The SMS verification screen's code entry: six bordered digit boxes the user
 * fills left-to-right. There's no design reference and no Tamagui primitive for
 * this, so it's built from scratch — but it deliberately reuses PSInput's visual
 * language (bordered `$base` box, hairline border, `$brand` active border,
 * `$error` red on error) so the two read as one family.
 *
 * The one architectural decision worth reading: this is NOT six inputs. It's
 * ONE hidden real `Input` (the actual TextInput — owns the value, the keyboard,
 * iOS/Android SMS autofill, paste-of-full-code, and backspace) sitting invisibly
 * ON TOP of six PRESENTATIONAL boxes. The boxes just render the digits of that
 * single value and highlight the next-to-fill cell as the "cursor". Six separate
 * focusable inputs with hand-managed focus-jumping is the fragile path everyone
 * regrets — backspace and paste both break — so we don't. Tapping the row
 * focuses the one hidden input.
 *
 * Errors come from TWO places, by design:
 *   - The SCREEN owns correctness. It verifies the code (against the server) and
 *     hands back an `error` message ("Incorrect code"); the atom just paints
 *     every box red and prints it. It never knows what makes a code correct.
 *   - The COMPONENT owns completeness. It defined the length (6), so "not full
 *     yet" is its OWN fact, not a business rule — so on blur-while-incomplete it
 *     shows its own "Please enter the complete code". This is the one bit of
 *     validation the atom can honestly own.
 * The screen's `error` takes precedence when both would show (a server rejection
 * is more specific than "incomplete").
 *
 * Focus/blur follow PSInput's clear-on-focus rhythm: on focus we clear our own
 * incomplete error AND fire `onFocus` up so the screen can clear its `error`
 * too; we also drop the caret at the end of the existing digits so typing
 * resumes at the last filled box. On blur we (a) set the incomplete error if the
 * code is partially filled, and (b) fire `onBlur` up. `onComplete(code)` still
 * fires the moment all six digits are in.
 */
const OTP_LENGTH = 6;

// The atom's own validation message — see the docstring (completeness is the
// atom's fact; correctness is the screen's).
const INCOMPLETE_MESSAGE = 'Please enter the complete code';

type PSOtpInputProps = {
  value: string;
  onChangeText: (value: string) => void;
  /**
   * Fired once, on the transition to all six digits — the screen verifies from
   * here. Verify the `code` ARGUMENT, not the screen's own `value` state: this
   * fires in the same tick as `onChangeText`, so the screen's state hasn't
   * flushed yet and would still read the pre-change (5-digit) value.
   */
  onComplete?: (code: string) => void;
  /**
   * Validation message from the screen (e.g. "Incorrect code"). Presence flips
   * every box into the error look and prints the message below. The atom doesn't
   * judge correctness — it's told the code was rejected. Takes precedence over
   * the atom's own incomplete error.
   */
  error?: string;
  disabled?: boolean;
  /**
   * Focus the hidden input once the SCREEN it lives on is focused (pops the
   * keyboard, highlights the first cell). For OTP screens where entering the
   * code is the only action.
   *
   * We do NOT pass RN's native `autoFocus` down: that fires on mount, which on a
   * pushed/presented screen collides with the routing animation and the focus
   * (and keyboard) silently gets dropped. Instead we focus imperatively from a
   * `useFocusEffect`, which runs after the navigation transition settles — so
   * the keyboard reliably comes up. See the ref/effect below.
   */
  autoFocus?: boolean;
  /** Fired when the field gains focus — the screen clears its `error` here. */
  onFocus?: () => void;
  /** Fired when the field loses focus (after the atom sets any incomplete error). */
  onBlur?: () => void;
};

export default function PSOtpInput({
  value,
  onChangeText,
  onComplete,
  error,
  disabled = false,
  autoFocus = false,
  onFocus,
  onBlur,
}: PSOtpInputProps) {
  const inputRef = useRef<TamaguiElement>(null);
  const [focused, setFocused] = useState(false);

  // Programmatic autoFocus (see the `autoFocus` prop docs): focus once the
  // screen is focused rather than on mount, so the keyboard doesn't get eaten by
  // the routing animation. Focusing fires the real onFocus → sets `focused` →
  // highlights the active cell, exactly as a tap would. useFocusEffect runs
  // after the transition settles and re-focuses on navigating back to the field.
  useFocusEffect(
    useCallback(() => {
      if (autoFocus) inputRef.current?.focus();
    }, [autoFocus])
  );
  // The atom's own completeness error (see docstring). Screen `error` wins over
  // it, so the displayed message is: server error, else our incomplete error.
  const [incompleteError, setIncompleteError] = useState<string | undefined>();
  // Caret position for the hidden input. Set to the end on focus so typing
  // resumes at the last filled box; cleared back to undefined after so normal
  // editing/selection isn't locked to the end.
  const [selection, setSelection] = useState<
    { start: number; end: number } | undefined
  >();
  const message = error ?? incompleteError;
  const hasError = !!message;

  // Digits-only, capped at six — the value handed up (and to onComplete) is
  // always a clean code. onComplete fires exactly on the transition to full.
  const handleChangeText = (text: string) => {
    const digits = text.replace(/\D/g, '').slice(0, OTP_LENGTH);
    // Any typing clears our own incomplete error (they're fixing it); the
    // screen owns clearing its `error`, which it does on focus.
    if (incompleteError) setIncompleteError(undefined);
    // Release the focus-forced caret once editing starts, so selection is the
    // input's own again (append-only anyway, but don't lock it).
    if (selection) setSelection(undefined);
    onChangeText(digits);
    // Edge-triggered: only on the TRANSITION to full, not on every full-length
    // event. `value` is the pre-change value, so `value.length < OTP_LENGTH`
    // means we weren't full before. Without this, a paste/autofill that
    // re-delivers a full code — or an at-cap keystroke maxLength-clamps back to
    // the same string — would re-fire onComplete and double-submit the verify.
    if (digits.length === OTP_LENGTH && value.length < OTP_LENGTH) {
      onComplete?.(digits);
    }
  };

  const handleFocus = () => {
    setFocused(true);
    setIncompleteError(undefined);
    // Drop the caret at the end so the next digit appends (the active cell is
    // value.length). Release the forced selection on the next tick's edits.
    setSelection({ start: value.length, end: value.length });
    onFocus?.();
  };

  const handleBlur = () => {
    setFocused(false);
    setSelection(undefined);
    // Partially filled and abandoned → surface the completeness error. Empty is
    // left alone (nothing started), full is valid.
    if (value.length > 0 && value.length < OTP_LENGTH) {
      setIncompleteError(INCOMPLETE_MESSAGE);
    }
    onBlur?.();
  };

  // The "cursor" cell: the first empty box, i.e. where the next digit lands.
  // Only highlighted while focused, and never when there's an error (error owns
  // the whole row's color then).
  const activeIndex = value.length;

  return (
    <YStack gap="$xs" alignSelf="stretch">
      {/* The row is the tap target: pressing any box focuses the one real input.
          The boxes below are purely presentational. */}
      <XStack
        gap="$sm"
        onPress={() => inputRef.current?.focus()}
        opacity={disabled ? 0.4 : 1}
      >
        {Array.from({ length: OTP_LENGTH }).map((_, i) => {
          const digit = value[i] ?? '';
          const isActive = focused && !hasError && i === activeIndex;
          const borderColor = hasError
            ? '$error'
            : isActive
              ? '$brand'
              : '$borderColor';
          return (
            <YStack
              // Fixed indices over a fixed-length list — no reorder, stable keys.
              key={i}
              flex={1}
              height={52}
              items="center"
              justify="center"
              rounded="$base"
              borderWidth="$borderWidth.hairline"
              borderColor={borderColor}
              backgroundColor="$background"
            >
              <Text
                fontFamily="$body"
                fontSize="$large"
                fontWeight="$semibold"
                color="$textDark"
              >
                {digit}
              </Text>
            </YStack>
          );
        })}
      </XStack>

      {/* The single real input, laid over the boxes but invisible: it owns the
          value + keyboard + SMS autofill + paste + backspace. `position:absolute`
          + zero opacity keeps it interactive (an unmounted/`display:none` input
          can't receive autofill) while the boxes are what's seen.
          `oneTimeCode` (iOS) + `sms-otp` (Android) light up SMS autofill.

          NB: deliberately NOT Tamagui's `<VisuallyHidden>`. That hides for
          accessibility — it sets `pointerEvents:'none'` (so the field can't be
          tapped/focused) and shrinks to a 1×1 clipped box (which suppresses SMS
          autofill on both platforms). We need the opposite: a full-size,
          focusable, autofill-able field that just happens to be transparent. So
          we keep it full row size at `opacity:0`, not visually-hidden. */}
      <Input
        ref={inputRef}
        value={value}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        selection={selection}
        disabled={disabled}
        keyboardType="number-pad"
        textContentType="oneTimeCode"
        autoComplete="sms-otp"
        maxLength={OTP_LENGTH}
        position="absolute"
        width="100%"
        height={52}
        opacity={0}
        // A caret would show through the transparent input over box #1; the
        // active-cell border is our cursor instead.
        caretHidden
      />

      {hasError && (
        <Text fontFamily="$body" fontSize="$small" color="$error">
          {message}
        </Text>
      )}
    </YStack>
  );
}
